import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { marketplaceAPI } from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import ServicesBrowse from "@/components/ServicesBrowse";
import PageTitle from "@/components/PageTitle";
import { CATEGORIES, CONDITIONS } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, SlidersHorizontal, PackageOpen, Plus, Package, Wrench } from "lucide-react";

export default function Marketplace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "services" ? "services" : "products";
  const setTab = (t) => setSearchParams(t === "services" ? { tab: "services" } : {});
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [condition, setCondition] = useState("all");
  const [sort, setSort] = useState("newest");

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 40, sort };
    if (search) params.search = search;
    if (category !== "all") params.category = category;
    if (condition !== "all") params.condition = condition;
    marketplaceAPI
      .getListings(params)
      .then((res) => setListings(res.data?.listings || []))
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, [search, category, condition, sort]);

  useEffect(() => {
    if (tab !== "products") return;
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, search, tab]);

  const Toggle = (
    <div className="inline-flex rounded-lg border border-border bg-card p-1" data-testid="marketplace-tabs">
      <button
        type="button"
        onClick={() => setTab("products")}
        data-testid="marketplace-tab-products"
        className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-colors ${tab === "products" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
      >
        <Package className="h-4 w-4" /> Products
      </button>
      <button
        type="button"
        onClick={() => setTab("services")}
        data-testid="marketplace-tab-services"
        className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-colors ${tab === "services" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
      >
        <Wrench className="h-4 w-4" /> Services
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageTitle
        title="Marketplace"
        subtitle="Buy & sell gear, hire & offer services"
      />

      {Toggle}

      {tab === "services" ? (
        <ServicesBrowse />
      ) : (
      <>
      <div className="flex items-center justify-end">
        <Button onClick={() => navigate("/marketplace/create")} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="marketplace-create-button">
          <Plus className="mr-2 h-4 w-4" /> Sell an item
        </Button>
      </div>
      <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search wheels, pedals, rigs..."
            className="pl-9"
            data-testid="marketplace-search-input"
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[160px]" data-testid="marketplace-category-select">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={condition} onValueChange={setCondition}>
            <SelectTrigger className="w-[140px]" data-testid="marketplace-condition-select">
              <SelectValue placeholder="Condition" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any condition</SelectItem>
              {CONDITIONS.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[150px]" data-testid="marketplace-sort-select">
              <SlidersHorizontal className="mr-1 h-3.5 w-3.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price_low">Price: Low to High</SelectItem>
              <SelectItem value="price_high">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[3/4] rounded-lg" />
          ))}
        </div>
      ) : listings.length ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="marketplace-grid">
          {listings.map((l) => (
            <ListingCard key={l.id} listing={l} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-20 text-center">
          <PackageOpen className="mb-3 h-12 w-12 text-muted-foreground" />
          <p className="font-medium">No listings found</p>
          <p className="text-sm text-muted-foreground">Try adjusting your filters or search.</p>
        </div>
      )}
      </>
      )}
    </div>
  );
}
